import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  Person24Regular,
  Location24Regular,
  Payment24Regular,
  Mail24Regular,
  Call24Regular,
  Shield24Regular,
  ArrowDownload24Regular,
} from '@fluentui/react-icons';
import { ActivityLogEntry } from '../types';
import { formatBusinessTime, formatBusinessDate } from '../../lib/businessTime';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  backButton: {
    color: tokens.colorNeutralForeground2,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.overflow('hidden'),
  },
  dateGroup: {
    ...shorthands.padding('12px', '20px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  dateGroupTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
  },
  activityItem: {
    display: 'flex',
    gap: '16px',
    ...shorthands.padding('16px', '20px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
    ':last-child': {
      borderBottom: 'none',
    },
  },
  activityIcon: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('8px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  activityIconProfile: {
    backgroundColor: tokens.colorPaletteBlueBorderActive,
    color: '#ffffff',
  },
  activityIconAddress: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: '#ffffff',
  },
  activityIconPayment: {
    backgroundColor: tokens.colorPalettePurpleBackground2,
    color: '#ffffff',
  },
  activityIconEmail: {
    backgroundColor: tokens.colorPaletteYellowBackground3,
    color: tokens.colorNeutralForeground1,
  },
  activityIconPhone: {
    backgroundColor: tokens.colorPaletteTealBackground2,
    color: '#ffffff',
  },
  activityIconPassword: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: '#ffffff',
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  activityDescription: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '4px',
  },
  activityChange: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    display: 'inline-block',
    marginTop: '4px',
  },
  activityMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '8px',
  },
  activityTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  activityActor: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('48px'),
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke3),
  },
  emptyState: {
    textAlign: 'center' as const,
    ...shorthands.padding('48px'),
    color: tokens.colorNeutralForeground3,
  },
});

// Mock activity data
const mockActivityLog: ActivityLogEntry[] = [
  {
    id: 'act-1',
    timestamp: new Date().toISOString(),
    action: 'update',
    entityType: 'address',
    description: 'Address Updated',
    changes: [
      { field: 'address1', oldValue: '123 Main St', newValue: '123 Main Street' },
    ],
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-2',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    action: 'create',
    entityType: 'payment_method',
    description: 'Payment Method Added',
    changes: [
      { field: 'card', newValue: 'Visa ending in 4242' },
    ],
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-3',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    action: 'update',
    entityType: 'email',
    description: 'Email Updated',
    changes: [
      { field: 'email', oldValue: 'old@email.com', newValue: 'carmen@example.com' },
    ],
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-4',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    action: 'update',
    entityType: 'phone',
    description: 'Phone Updated',
    changes: [
      { field: 'phone', oldValue: '(787) 555-0000', newValue: '(787) 555-1234' },
    ],
    actor: { type: 'admin', name: 'TWM Admin (Support request #12345)', id: 'admin-1' },
  },
  {
    id: 'act-5',
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    action: 'update',
    entityType: 'password',
    description: 'Password Changed',
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-6',
    timestamp: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    action: 'create',
    entityType: 'address',
    description: 'Address Added',
    changes: [
      { field: 'address', newValue: '456 Oak Avenue, Bayamón, PR 00959' },
    ],
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
  {
    id: 'act-7',
    timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    action: 'create',
    entityType: 'profile',
    description: 'Account Created',
    actor: { type: 'customer', name: 'You', id: 'cust-1' },
  },
];

const ActivityLog: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);

  useEffect(() => {
    const loadActivityLog = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        setActivities(mockActivityLog);
      } finally {
        setLoading(false);
      }
    };
    loadActivityLog();
  }, []);

  const getActivityIcon = (entityType: string) => {
    switch (entityType) {
      case 'profile':
        return (
          <div className={`${styles.activityIcon} ${styles.activityIconProfile}`}>
            <Person24Regular />
          </div>
        );
      case 'address':
        return (
          <div className={`${styles.activityIcon} ${styles.activityIconAddress}`}>
            <Location24Regular />
          </div>
        );
      case 'payment_method':
        return (
          <div className={`${styles.activityIcon} ${styles.activityIconPayment}`}>
            <Payment24Regular />
          </div>
        );
      case 'email':
        return (
          <div className={`${styles.activityIcon} ${styles.activityIconEmail}`}>
            <Mail24Regular />
          </div>
        );
      case 'phone':
        return (
          <div className={`${styles.activityIcon} ${styles.activityIconPhone}`}>
            <Call24Regular />
          </div>
        );
      case 'password':
        return (
          <div className={`${styles.activityIcon} ${styles.activityIconPassword}`}>
            <Shield24Regular />
          </div>
        );
      default:
        return (
          <div className={styles.activityIcon}>
            <Person24Regular />
          </div>
        );
    }
  };

  const formatTime = (dateString: string) => formatBusinessTime(dateString);

  const getDateGroup = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return formatBusinessDate(dateString);
  };

  // Group activities by date
  const groupedActivities = activities.reduce((groups, activity) => {
    const dateGroup = getDateGroup(activity.timestamp);
    if (!groups[dateGroup]) {
      groups[dateGroup] = [];
    }
    groups[dateGroup].push(activity);
    return groups;
  }, {} as Record<string, ActivityLogEntry[]>);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading activity log..." />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          className={styles.backButton}
          onClick={() => navigate('/portal/account')}
        />
        <div className={styles.headerContent}>
          <Text className={styles.title}>Activity Log</Text>
          <Text className={styles.subtitle}>History of all changes made to your profile</Text>
        </div>
        <Button appearance="subtle" icon={<ArrowDownload24Regular />}>
          Export
        </Button>
      </div>

      {/* Activity List */}
      <div className={styles.card}>
        {activities.length === 0 ? (
          <div className={styles.emptyState}>
            <Text>No activity recorded yet</Text>
          </div>
        ) : (
          Object.entries(groupedActivities).map(([dateGroup, groupActivities]) => (
            <div key={dateGroup}>
              <div className={styles.dateGroup}>
                <Text className={styles.dateGroupTitle}>{dateGroup}</Text>
              </div>
              {groupActivities.map((activity) => (
                <div key={activity.id} className={styles.activityItem}>
                  {getActivityIcon(activity.entityType)}
                  <div className={styles.activityContent}>
                    <Text className={styles.activityTitle}>{activity.description}</Text>
                    {activity.changes?.map((change, index) => (
                      <div key={index}>
                        {change.oldValue && change.newValue ? (
                          <Text className={styles.activityDescription}>
                            Changed: {change.oldValue} → {change.newValue}
                          </Text>
                        ) : change.newValue ? (
                          <Text className={styles.activityDescription}>
                            {change.newValue}
                          </Text>
                        ) : null}
                      </div>
                    ))}
                    <div className={styles.activityMeta}>
                      <Text className={styles.activityTime}>{formatTime(activity.timestamp)}</Text>
                      <Text className={styles.activityActor}>By: {activity.actor.name}</Text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}

        {activities.length > 0 && (
          <div className={styles.loadMore}>
            <Button appearance="subtle">Load More</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
